import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { C, S } from '../../utils/styles';
import Icon from '../Icon';
import api from '../../utils/api';
import { SUPPORT_TEAM, SUPPORT_TEAM_BY_ID, RECIPIENT_ACCOUNT_MANAGER } from '../../utils/supportTeam';

/**
 * Chat messaging panel backed by /api/clients/{id}/messages.
 *
 * Threads:
 *   - "all"            — every message on this client (default in CRM/internal mode)
 *   - "account_manager"— messages addressed to the assigned AM (default in portal mode)
 *   - "<support id>"   — angie_comer / cassie_nix / kate_hawn personal threads
 *
 * Each contact has a "personal message system" — messages are filtered + sent
 * with a `recipientId`. The same backend collection backs all threads; we
 * just slice by recipientId.
 */
export default function MessagesSection({ clientId, currentUserId, variant = 'portal', compact = false }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeThread, setActiveThread] = useState(variant === 'portal' ? RECIPIENT_ACCOUNT_MANAGER : 'all');
  const threadRef = useRef(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const list = await api.messages.list(clientId);
      setMessages(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  // SavedOptions AM-CTA prefill — drop into the input + switch to AM thread
  useEffect(() => {
    if (!clientId || variant !== 'portal') return undefined;
    try {
      const stored = sessionStorage.getItem(`s105-prefill-${clientId}`);
      if (stored) {
        setDraft(stored);
        setActiveThread(RECIPIENT_ACCOUNT_MANAGER);
        sessionStorage.removeItem(`s105-prefill-${clientId}`);
      }
    } catch (err) { /* ignore */ }
    return undefined;
  }, [clientId, variant]);

  // Filter messages by active thread
  const visibleMessages = useMemo(() => {
    if (activeThread === 'all') return messages;
    return messages.filter((m) => {
      const r = m.recipientId || RECIPIENT_ACCOUNT_MANAGER; // legacy messages default to AM thread
      return r === activeThread;
    });
  }, [messages, activeThread]);

  useEffect(() => {
    // Mark visible messages as read on mount / update
    if (!clientId || visibleMessages.length === 0) return;
    const hasUnread = visibleMessages.some(m => !(m.readBy || []).includes(currentUserId));
    if (hasUnread) {
      api.messages.markRead(clientId).catch(() => {});
    }
  }, [clientId, visibleMessages, currentUserId]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [visibleMessages, activeThread]);

  const handleSend = async () => {
    if (!draft.trim() || sending) return;
    try {
      setSending(true);
      const recipientId = activeThread === 'all' ? null : activeThread;
      const newMsg = await api.messages.send(clientId, draft.trim(), recipientId);
      setDraft('');
      setMessages(prev => [...prev, newMsg]);
    } catch (err) {
      console.error('Send failed:', err);
    } finally {
      setSending(false);
    }
  };

  const unreadByThread = useMemo(() => {
    const byT = { all: 0, [RECIPIENT_ACCOUNT_MANAGER]: 0 };
    SUPPORT_TEAM.forEach((t) => { byT[t.id] = 0; });
    messages.forEach((m) => {
      if (m.senderId === currentUserId) return;
      if ((m.readBy || []).includes(currentUserId)) return;
      byT.all += 1;
      const r = m.recipientId || RECIPIENT_ACCOUNT_MANAGER;
      byT[r] = (byT[r] || 0) + 1;
    });
    return byT;
  }, [messages, currentUserId]);
  const unreadCount = unreadByThread.all;

  const activeContact = activeThread === RECIPIENT_ACCOUNT_MANAGER
    ? { id: 'account_manager', name: 'Your Account Manager', role: 'Account Manager', email: '', phone: '', color: C.blue }
    : SUPPORT_TEAM_BY_ID[activeThread];

  return (
    <div data-testid="messages-section" style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="chatbubbles-outline" style={{ fontSize: 20, color: C.accent }} />
          <h2 style={{ fontSize: 15, fontWeight: 800, color: C.text, margin: 0, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>
            {variant === 'portal' ? 'Messages with your HealthCues team' : 'Messages with Client'}
          </h2>
        </div>
        {unreadCount > 0 && (
          <span data-testid="messages-unread-badge" style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#dc2626', padding: '2px 8px', borderRadius: 999 }}>
            {unreadCount} new
          </span>
        )}
      </div>

      {/* Thread switcher */}
      <div style={{ display: 'flex', gap: 4, padding: '8px 12px', borderBottom: `1px solid ${C.borderLight}`, background: '#fafbfc', overflowX: 'auto' }}>
        {variant !== 'portal' && (
          <ThreadTab id="all" label="All" active={activeThread === 'all'} unread={unreadByThread.all} onClick={setActiveThread} />
        )}
        <ThreadTab
          id={RECIPIENT_ACCOUNT_MANAGER}
          label="Account Manager"
          active={activeThread === RECIPIENT_ACCOUNT_MANAGER}
          unread={unreadByThread[RECIPIENT_ACCOUNT_MANAGER]}
          onClick={setActiveThread}
        />
        {SUPPORT_TEAM.map((m) => (
          <ThreadTab
            key={m.id}
            id={m.id}
            label={m.name.split(' ')[0]}
            active={activeThread === m.id}
            unread={unreadByThread[m.id]}
            onClick={setActiveThread}
            color={m.color}
          />
        ))}
      </div>

      {/* Recipient indicator */}
      {activeContact && (
        <div style={{ padding: '8px 20px', background: C.bg, borderBottom: `1px solid ${C.borderLight}`, display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
          <span style={{ width: 28, height: 28, borderRadius: '50%', background: activeContact.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 11, flexShrink: 0 }}>
            {initials(activeContact.name)}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: C.text }}>{activeContact.name}</div>
            <div style={{ fontSize: 11, color: C.textMid }}>
              {activeContact.role}
              {activeContact.email && ` · ${activeContact.email}`}
              {activeContact.phone && ` · ${activeContact.phone}`}
            </div>
          </div>
        </div>
      )}

      <div
        ref={threadRef}
        data-testid="messages-thread"
        style={{ maxHeight: compact ? 260 : 360, minHeight: 160, overflowY: 'auto', padding: '14px 20px', background: C.bg }}
      >
        {loading ? (
          <div style={{ fontSize: 12, color: C.textLight, textAlign: 'center', padding: 20 }}>Loading…</div>
        ) : visibleMessages.length === 0 ? (
          <div style={{ fontSize: 13, color: C.textLight, textAlign: 'center', padding: 30 }}>
            <Icon name="chatbubble-ellipses-outline" style={{ fontSize: 28, marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
            {activeContact ? `No messages with ${activeContact.name} yet — say hi!` : 'No messages yet. Say hi to your HealthCues team!'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {visibleMessages.map(m => (
              <MessageBubble key={m.id} msg={m} isMine={m.senderId === currentUserId} />
            ))}
          </div>
        )}
      </div>

      <div style={{ padding: 14, borderTop: `1px solid ${C.border}`, display: 'flex', gap: 8, background: '#fff' }}>
        <input
          data-testid="message-input"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder={activeContact ? `Message ${activeContact.name}…` : 'Type a message…'}
          disabled={sending}
          style={{ flex: 1, padding: '10px 14px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
        />
        <button
          type="button"
          data-testid="message-send-btn"
          onClick={handleSend}
          disabled={!draft.trim() || sending}
          style={{
            padding: '10px 18px',
            background: draft.trim() && !sending ? C.accent : C.textLight,
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 700,
            cursor: draft.trim() && !sending ? 'pointer' : 'not-allowed',
          }}
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

function ThreadTab({ id, label, active, unread, onClick, color }) {
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      data-testid={`messages-thread-tab-${id}`}
      style={{
        padding: '6px 12px',
        background: active ? (color || C.accent) : 'transparent',
        color: active ? '#fff' : C.textMid,
        border: 'none',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexShrink: 0,
      }}
    >
      {label}
      {unread > 0 && (
        <span style={{ background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 800, padding: '1px 5px', borderRadius: 999, minWidth: 16, textAlign: 'center' }}>
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </button>
  );
}

function MessageBubble({ msg, isMine }) {
  const bgColor = msg.senderType === 'system'
    ? '#fefce8'
    : msg.senderType === 'internal'
      ? '#fff'
      : C.accentBg;
  const avatarColor = msg.senderType === 'system'
    ? C.orange
    : msg.senderType === 'internal'
      ? C.blue
      : C.green;
  const timeLabel = formatRelative(msg.createdAt);
  const recipientLabel = msg.recipientId
    ? (SUPPORT_TEAM_BY_ID[msg.recipientId]?.name || (msg.recipientId === RECIPIENT_ACCOUNT_MANAGER ? 'Account Manager' : msg.recipientId))
    : null;

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexDirection: isMine ? 'row-reverse' : 'row' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: avatarColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
        {msg.senderAvatar || '?'}
      </div>
      <div style={{ flex: 1, maxWidth: '80%' }}>
        <div style={{ background: bgColor, border: `1px solid ${C.border}`, padding: '10px 14px', borderRadius: 12, fontSize: 13, color: C.text, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {msg.body}
        </div>
        <div style={{ fontSize: 10, color: C.textLight, marginTop: 4, display: 'flex', gap: 6, alignItems: 'center', flexDirection: isMine ? 'row-reverse' : 'row', flexWrap: 'wrap' }}>
          <strong style={{ color: C.textMid, fontWeight: 700 }}>{msg.senderName}</strong>
          {msg.senderRole && <span>· {msg.senderRole}</span>}
          <span>· {timeLabel}</span>
          {recipientLabel && isMine && <span>· to {recipientLabel}</span>}
        </div>
      </div>
    </div>
  );
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
  return d.toLocaleDateString();
}
